import {useState, useEffect} from 'react'
import styled from 'styled-components/macro'
import {X} from 'react-feather'
import {isMobile} from 'react-device-detect'
import {UnsupportedChainIdError} from '@web3-react/core'
import {AbstractConnector} from '@web3-react/abstract-connector'
import {TEXT} from '../../theme/theme'
import {useActiveWeb3React} from '../../hooks/web3'
import {injected} from '../../connectors/connectors'
import {SUPPORTED_WALLETS} from '../../constants/wallet'
import {ApplicationModal} from '../../state/application/actions'
import {useModalOpen, useWalletModalToggle} from '../../state/application/hooks'
import Modal from '../Modal/Modal'
import PendingView from './PendingView'
import WalletOption from './WalletOptions'
import usePrevious from '../../hooks/usePrevious'
import METAMASK_ICON from '../../assets/images/metamask.png'
import {switchNetworkTo, switchNetworkToArbitrum} from '../../utils/switchNetworkToArbitrum'
import {useMultichainContext} from '../../state/multichain/useMultichainContext'
import {ampli} from '../../ampli'

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
`

export const WalletHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

export const CloseIcon = styled.div`
  :hover {
    cursor: pointer;
    opacity: 0.7;
  }
`

const WALLET_VIEWS = {
  OPTIONS: 'options',
  OPTIONS_SECONDARY: 'options_secondary',
  ACCOUNT: 'account',
  PENDING: 'pending',
}

export function trackWalletConnection(address?: string) {
  ampli.identify(address)
  ampli.walletConnected({wallet_connected: true})
}

export default function ConnectWalletModal() {
  const {account, connector, activate} = useActiveWeb3React()
  const {chainId: contextChainId} = useMultichainContext()
  const walletModalOpen = useModalOpen(ApplicationModal.WALLET)

  const toggleWalletModal = useWalletModalToggle()

  const [walletView, setWalletView] = useState(WALLET_VIEWS.ACCOUNT)

  const [pendingWallet, setPendingWallet] = useState<AbstractConnector | undefined>()

  const [pendingError, setPendingError] = useState<boolean>()

  const previousAccount = usePrevious(account)

  // close on connection, when logged out before
  useEffect(() => {
    if (account && !previousAccount && walletModalOpen) {
      toggleWalletModal()
    }
  }, [account, previousAccount, toggleWalletModal, walletModalOpen])

  // always reset to account view
  useEffect(() => {
    if (walletModalOpen) {
      setPendingError(false)
      setWalletView(WALLET_VIEWS.ACCOUNT)
    }
  }, [walletModalOpen])

  const tryActivation = async (connector: AbstractConnector | undefined) => {
    Object.keys(SUPPORTED_WALLETS).map(key => {
      if (connector === SUPPORTED_WALLETS[key].connector) {
        return SUPPORTED_WALLETS[key].name
      }
      return true
    })
    setPendingWallet(connector) // set wallet for pending view
    setWalletView(WALLET_VIEWS.PENDING)

    // if the connector is walletconnect and the user has already tried to connect, manually reset the connector
    // if (connector instanceof WalletConnectConnector && connector.walletConnectProvider?.wc?.uri) {
    //   connector.walletConnectProvider = undefined
    // }

    connector &&
      activate(connector, undefined, true)
        .catch(error => {
          if (error instanceof UnsupportedChainIdError) {
            switchNetworkToArbitrum()
            activate(connector) // a little janky...can't use setError because the connector isn't set
              .then(() => connector.getAccount().then(connectedAccount => trackWalletConnection(connectedAccount ?? undefined)))
          } else {
            setPendingError(true)
          }
        })
        .then(() => {
          localStorage.setItem('disconnected', 'false')
          connector.getAccount().then(connectedAccount => trackWalletConnection(connectedAccount ?? undefined))
          contextChainId && switchNetworkTo(contextChainId)
        })
  }

  function getOptions() {
    const isMetamask = window.ethereum && window.ethereum.isMetaMask

    return Object.keys(SUPPORTED_WALLETS).map(key => {
      const option = SUPPORTED_WALLETS[key]
      // check for mobile options
      if (isMobile) {
        if (option.mobile) {
          return (
            <WalletOption
              onClick={() => {
                option.connector !== connector && !option.href && tryActivation(option.connector)
              }}
              id={`connect-${key}`}
              key={key}
              active={option.connector && option.connector === connector}
              link={option.href}
              header={option.name}
              subheader={null}
              icon={option.iconURL}
            />
          )
        }
        return null
      }

      // overwrite injected when needed
      if (option.connector === injected) {
        // don't show injected if there's no injected provider
        if (!(window.web3 || window.ethereum)) {
          if (option.name === 'MetaMask') {
            return (
              <WalletOption
                id={`connect-${key}`}
                key={key}
                header={'Install Metamask'}
                subheader={null}
                link={'https://metamask.io/'}
                icon={METAMASK_ICON}
              />
            )
          } else {
            return null //dont want to return install twice
          }
        }
        // don't return metamask if injected provider isn't metamask
        else if (option.name === 'MetaMask' && !isMetamask) {
          return null
        }
        // likewise for generic
        else if (option.name === 'Injected' && isMetamask) {
          return null
        }
      }

      // return rest of options
      return (
        !isMobile &&
        !option.mobileOnly && (
          <WalletOption
            id={`connect-${key}`}
            onClick={() => {
              option.connector === connector ? setWalletView(WALLET_VIEWS.ACCOUNT) : !option.href && tryActivation(option.connector)
            }}
            key={key}
            active={option.connector === connector}
            link={option.href}
            header={option.name}
            subheader={null} //use option.descriptio to bring back multi-line
            icon={option.iconURL}
          />
        )
      )
    })
  }

  return (
    <Modal isOpen={walletModalOpen} onDismiss={toggleWalletModal} minHeight={false} maxHeight={90} width="350px">
      <ModalContent>
        <WalletHeader>
          <TEXT.StandardBody color={'white'} fontWeight={600} m={'auto 0'}>
            Connect to a wallet
          </TEXT.StandardBody>
          <CloseIcon onClick={toggleWalletModal}>
            <X color={'white'} height={24} width={24} />
          </CloseIcon>
        </WalletHeader>
        {walletView === WALLET_VIEWS.PENDING ? (
          <PendingView connector={pendingWallet} error={pendingError} setPendingError={setPendingError} tryActivation={tryActivation} />
        ) : (
          <>{getOptions()}</>
        )}
      </ModalContent>
    </Modal>
  )
}
